import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Seo } from '@/components/Seo';
import { ICONS, Icon, InfoShell } from './InfoShell';

// New-design FAQ (/faq): a search box, topic list on the side (sticky on
// desktop) and smooth accordions. Same i18n keys and FAQPage schema as the
// classic page.

const GROUPS = [
  { id: 'A', n: 5, icon: ICONS.flask },
  { id: 'B', n: 3, icon: ICONS.shield },
  { id: 'C', n: 3, icon: ICONS.truck },
  { id: 'D', n: 3, icon: ICONS.building },
];

export default function FaqV2() {
  const { t } = useTranslation();
  const [open, setOpen] = useState<string | null>('A-1');
  const [q, setQ] = useState('');

  const groups = useMemo(
    () =>
      GROUPS.map((g) => ({
        ...g,
        title: t(`faq.group${g.id}.title`),
        items: Array.from({ length: g.n }, (_, i) => ({ key: `${g.id}-${i + 1}`, q: t(`faq.group${g.id}.q${i + 1}.question`), a: t(`faq.group${g.id}.q${i + 1}.answer`) })),
      })),
    [t],
  );
  const needle = q.trim().toLowerCase();
  const shown = groups.map((g) => ({ ...g, items: needle ? g.items.filter((it) => (it.q + ' ' + it.a).toLowerCase().includes(needle)) : g.items })).filter((g) => g.items.length);
  const total = shown.reduce((s, g) => s + g.items.length, 0);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: groups.flatMap((g) => g.items.map((it) => ({ '@type': 'Question', name: it.q, acceptedAnswer: { '@type': 'Answer', text: it.a } }))),
  };

  const mark = (text: string) => {
    if (!needle) return text;
    const i = text.toLowerCase().indexOf(needle);
    if (i < 0) return text;
    return (
      <>
        {text.slice(0, i)}
        <mark>{text.slice(i, i + needle.length)}</mark>
        {text.slice(i + needle.length)}
      </>
    );
  };

  return (
    <>
      <Seo pageKey="faq" jsonLd={schema} />
      <InfoShell
        label={t('faq.hero.label')}
        title={t('faq.hero.heading')}
        lede={t('faq.hero.subtitle')}
        meta={
          <label className="fsearch">
            <Icon d={ICONS.search} />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('infoV2.faq.search', 'Search the questions…')}
              aria-label={t('infoV2.faq.search', 'Search the questions…')}
            />
            {q && (
              <button type="button" onClick={() => setQ('')} aria-label="Clear">
                <Icon d={ICONS.close} />
              </button>
            )}
          </label>
        }
      >
        <section className="isec">
          <div className="wrap flay">
            <aside className="fnav">
              <span className="lbl">{t('infoV2.faq.topics', 'Topics')}</span>
              {groups.map((g) => {
                const n = shown.find((s) => s.id === g.id)?.items.length ?? 0;
                return (
                  <a
                    key={g.id}
                    href={'#faq-' + g.id}
                    className={n ? '' : 'off'}
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById('faq-' + g.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                  >
                    <Icon d={g.icon} />
                    <span>{g.title}</span>
                    <em>{n}</em>
                  </a>
                );
              })}
            </aside>

            <div className="flist">
              {needle && (
                <p className="fres">
                  {total
                    ? t('infoV2.faq.results', '{{n}} answers for “{{q}}”', { n: total, q: q.trim() })
                    : t('infoV2.faq.none', 'No answers for “{{q}}”. Try another word, or ask us below.', { q: q.trim() })}
                </p>
              )}
              {shown.map((g) => (
                <div key={g.id} id={'faq-' + g.id} className="fgrp rv">
                  <h2>
                    <span className="ic">
                      <Icon d={g.icon} />
                    </span>
                    {g.title}
                  </h2>
                  {g.items.map((it) => {
                    const isOpen = open === it.key || !!needle;
                    return (
                      <div key={it.key} className={`fq${isOpen ? ' on' : ''}`}>
                        <button type="button" aria-expanded={isOpen} onClick={() => setOpen(open === it.key ? null : it.key)}>
                          <span>{mark(it.q)}</span>
                          <i>
                            <Icon d={ICONS.plus} />
                          </i>
                        </button>
                        <div className="fqa">
                          <div>
                            <p>{mark(it.a)}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </section>
      </InfoShell>
    </>
  );
}
